import { clsx } from 'clsx';
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import type { MockRegistration } from '../../data/mockRegistrations';

type MosaicGalleryProps = {
  records: MockRegistration[];
  mode?: 'trophy' | 'grid';
  pageSize?: number;
  filter?: string;
  initialPage?: number;
};

const TROPHY_MASK = "url('/images/mosaic/trophy-silhouette.svg')";
const TROPHY_TILES = 84;

export function MosaicGallery({
  records,
  mode = 'trophy',
  pageSize = 36,
  filter = '',
  initialPage = 1,
}: MosaicGalleryProps) {
  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return records;
    return records.filter(
      (r) =>
        r.displayName.toLowerCase().includes(q) ||
        r.message.toLowerCase().includes(q),
    );
  }, [records, filter]);

  const trophyTiles = useMemo(() => filtered.slice(0, TROPHY_TILES), [filtered]);

  const [page, setPage] = useState(initialPage);
  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const currentPage = Math.min(page, totalPages);
  const pageStart = (currentPage - 1) * pageSize;
  const pageItems = useMemo(
    () => filtered.slice(pageStart, pageStart + pageSize),
    [filtered, pageStart, pageSize],
  );

  if (filtered.length === 0) {
    return (
      <div className="rounded-md border border-gold/20 bg-night/40 p-10 text-center">
        <p className="font-mono text-xs uppercase tracking-stamp text-champagne">
          Nenhuma presença encontrada
        </p>
        <p className="mt-2 text-sm text-neutral-100">
          Tente buscar por outro nome ou limpe o filtro.
        </p>
      </div>
    );
  }

  if (mode === 'trophy') {
    return (
      <div className="relative mx-auto aspect-[5/7] w-full max-w-2xl">
        <div
          aria-hidden="true"
          className="absolute inset-0 bg-gold-gradient opacity-30 blur-2xl"
        />
        <div
          aria-hidden="true"
          className="absolute inset-0"
          style={{
            background:
              'radial-gradient(ellipse at 50% 40%, rgba(212,175,55,0.4) 0%, transparent 70%)',
          }}
        />

        <div
          className="absolute inset-0 grid grid-cols-12 gap-0.5"
          style={{
            WebkitMaskImage: TROPHY_MASK,
            maskImage: TROPHY_MASK,
            WebkitMaskRepeat: 'no-repeat',
            maskRepeat: 'no-repeat',
            WebkitMaskSize: 'contain',
            maskSize: 'contain',
            WebkitMaskPosition: 'center',
            maskPosition: 'center',
          }}
        >
          {trophyTiles.map((reg) => (
            <Link
              key={reg.id}
              to={`/c/${reg.id}`}
              className="group relative aspect-square overflow-hidden bg-bronze/30 focus:outline-none focus-visible:ring-2 focus-visible:ring-gold"
              aria-label={`${reg.displayName}: ${reg.message}`}
            >
              <img
                src={reg.photoUrl}
                alt={reg.displayName}
                loading="lazy"
                decoding="async"
                className="h-full w-full object-cover transition-transform duration-base group-hover:scale-110"
              />
              <span
                aria-hidden="true"
                className="absolute inset-0 bg-gradient-to-t from-night/40 to-transparent opacity-0 transition-opacity duration-base group-hover:opacity-100"
              />
            </Link>
          ))}
        </div>

        <div
          aria-hidden="true"
          className="absolute inset-0"
          style={{
            background: 'linear-gradient(180deg, transparent 0%, rgba(212,175,55,0.15) 100%)',
            WebkitMaskImage: TROPHY_MASK,
            maskImage: TROPHY_MASK,
            WebkitMaskRepeat: 'no-repeat',
            maskRepeat: 'no-repeat',
            WebkitMaskSize: 'contain',
            maskSize: 'contain',
            WebkitMaskPosition: 'center',
            maskPosition: 'center',
            mixBlendMode: 'overlay',
          }}
        />
      </div>
    );
  }

  // mode === 'grid'
  return (
    <div className="flex flex-col gap-8">
      <ul className="grid grid-cols-2 gap-3 sm:grid-cols-3 sm:gap-4 md:grid-cols-4 lg:grid-cols-6">
        {pageItems.map((reg) => (
          <li key={reg.id}>
            <Link
              to={`/c/${reg.id}`}
              className="group block overflow-hidden rounded-md border border-gold/20 bg-parchment text-night transition-all duration-base hover:border-gold hover:shadow-goldSoft focus:outline-none focus-visible:ring-2 focus-visible:ring-gold"
              aria-label={`${reg.displayName}: ${reg.message}`}
            >
              <div className="relative aspect-square overflow-hidden bg-bronze/20">
                <img
                  src={reg.photoUrl}
                  alt={reg.displayName}
                  loading="lazy"
                  decoding="async"
                  className="h-full w-full object-cover transition-transform duration-base group-hover:scale-105"
                />
              </div>
              <div className="flex flex-col gap-0.5 px-3 py-2">
                <p className="truncate font-display text-sm text-night">{reg.displayName}</p>
                <p className="line-clamp-1 text-[10px] italic text-bronze">“{reg.message}”</p>
              </div>
            </Link>
          </li>
        ))}
      </ul>

      {totalPages > 1 ? (
        <Paginator
          currentPage={currentPage}
          totalPages={totalPages}
          onChange={(p) => {
            setPage(p);
            // mantém o usuário próximo do topo do grid
            window.scrollTo({ top: window.scrollY - 80, behavior: 'smooth' });
          }}
        />
      ) : null}
    </div>
  );
}

function Paginator({
  currentPage,
  totalPages,
  onChange,
}: {
  currentPage: number;
  totalPages: number;
  onChange: (page: number) => void;
}) {
  const pages = buildPageList(currentPage, totalPages);
  const baseBtn =
    'inline-flex h-9 min-w-[2.25rem] items-center justify-center rounded-md border px-2 font-mono text-xs uppercase tracking-stamp transition-colors duration-fast';

  return (
    <nav aria-label="Paginação da galeria" className="flex flex-wrap items-center justify-center gap-2">
      <button
        type="button"
        onClick={() => onChange(Math.max(1, currentPage - 1))}
        disabled={currentPage === 1}
        className={clsx(
          baseBtn,
          'border-gold/30 text-champagne hover:border-gold hover:text-gold disabled:cursor-not-allowed disabled:opacity-30',
        )}
        aria-label="Página anterior"
      >
        ‹
      </button>

      {pages.map((p, i) =>
        p === '…' ? (
          <span
            key={`gap-${i}`}
            aria-hidden="true"
            className="px-1 font-mono text-xs text-champagne/50"
          >
            …
          </span>
        ) : (
          <button
            key={p}
            type="button"
            onClick={() => onChange(p)}
            aria-current={p === currentPage ? 'page' : undefined}
            className={clsx(
              baseBtn,
              p === currentPage
                ? 'border-gold bg-gold/10 text-gold shadow-goldSoft'
                : 'border-gold/30 text-champagne hover:border-gold hover:text-gold',
            )}
          >
            {p}
          </button>
        ),
      )}

      <button
        type="button"
        onClick={() => onChange(Math.min(totalPages, currentPage + 1))}
        disabled={currentPage === totalPages}
        className={clsx(
          baseBtn,
          'border-gold/30 text-champagne hover:border-gold hover:text-gold disabled:cursor-not-allowed disabled:opacity-30',
        )}
        aria-label="Próxima página"
      >
        ›
      </button>
    </nav>
  );
}

function buildPageList(current: number, total: number): (number | '…')[] {
  if (total <= 7) {
    return Array.from({ length: total }, (_, i) => i + 1);
  }
  const pages: (number | '…')[] = [1];
  if (current > 3) pages.push('…');
  for (let p = Math.max(2, current - 1); p <= Math.min(total - 1, current + 1); p++) {
    pages.push(p);
  }
  if (current < total - 2) pages.push('…');
  pages.push(total);
  return pages;
}
